import React, { useEffect, useState } from "react";
import {
  useCurrencyFormatter,
  useDateFormatter,
  useLabelsWithKrn,
  useRetailer,
} from "hooks";
import { getBiller } from "selectors";
import { useSelector } from "react-redux";
import { Banner, Tooltip, TooltipEnums } from "kubra-ux-forge";
import { useTranslation } from "react-i18next";
import {
  BALANCE_NAMESPACE,
  RECENT_PAYMENTS_NAMESPACE,
} from "constants/i18n-namespaces";
import { ContactAndTocFooter } from "components/ezpay/common/contact-and-toc-footer";
import { CashView } from "../common/retail-cash-flow/cash-view";
import FundingOption from "constants/FundingOption";
import { getLabelWithKrn } from "utilities/helper";
import { getBannerRole } from "utilities/banner-accessibility";
import { getIsMobile } from "selectors/appState";
import { PayableCashSkeleton } from "components/ezpay/common/payable-cash-skeleton";
import { getPayableSession } from "selectors/payableSession";
import { getSelectedPayableAmountOption } from "selectors/paymentIntent";
import { AccountPayable, InvoicePayable } from "types/payable";
import { PayableBreadcrumb } from "components/ezpay/common/payable-breadcrumb";

export const PayableCash = () => {
  const { t, i18n } = useTranslation([
    BALANCE_NAMESPACE,
    RECENT_PAYMENTS_NAMESPACE,
  ]);
  const payableSession = useSelector(getPayableSession);
  const isMobile = useSelector(getIsMobile);
  const currencyFormatter = useCurrencyFormatter();
  const dateFormatter = useDateFormatter();
  const { getLabelsWithKrn } = useLabelsWithKrn();
  const labelSourceWithKrn = getLabelsWithKrn();
  const finalBiller = useSelector(getBiller);

  const { loadBillerConfiguration } = useRetailer();
  const [emailBannerState, setEmailBannerState] = useState(false);
  const [emailAddress, setEmailAddress] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [smsBannerState, setSmsBannerState] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [loadingStates, setLoadingStates] = useState({
    loadConfig: true,
  });

  // get required values from subresources
  const imageResources: any = finalBiller?.properties?.find((element: any) => {
    return element.name === "ImageResources";
  });

  const selectedAmountOption = useSelector(getSelectedPayableAmountOption);
  const selectedAmountOptionLabel =
    payableSession?.payable?.category === "invoice" && selectedAmountOption
      ? getLabelWithKrn(
          labelSourceWithKrn.labelsWithKrn,
          payableSession?.payable?.typeKrn ?? null,
          selectedAmountOption.labelKey,
        )
      : undefined;

  const isRcpOnly =
    !payableSession?.paymentIntentId &&
    payableSession?.fundingOptions?.length === 1 &&
    payableSession?.fundingOptions[0] === FundingOption.Cash;

  const updateLoadingState = (key: string, value: boolean) => {
    setLoadingStates((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  useEffect(() => {
    // Load config
    const loadConfig = async () => {
      if (finalBiller !== null && finalBiller !== undefined) {
        try {
          await loadBillerConfiguration(
            finalBiller.clientId,
            payableSession.instanceInfo.realm,
          );
        } catch (error) {
          console.log("Error loading config:", error);
        } finally {
          updateLoadingState("loadConfig", false);
        }
      } else {
        updateLoadingState("loadConfig", false);
      }
    };

    loadConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalBiller]);

  // Monitoring loadingStates and updating isDataLoading accordingly
  useEffect(() => {
    const allDone = Object.values(loadingStates).every(
      (state) => state === false,
    );
    console.log("All done result :", allDone);
    if (allDone) {
      setIsDataLoading(false);
    }
  }, [loadingStates]);

  const setCashView = (method: string) => {
    const stateMap: Record<string, { email: boolean; sms: boolean }> = {
      CashView: { email: false, sms: false },
      EmailModalSuccessClose: { email: true, sms: false },
      SmsModalSuccessClose: { email: false, sms: true },
    };

    const { email, sms } = stateMap[method] || { email: false, sms: false };

    setEmailBannerState(email);
    setSmsBannerState(sms);
  };

  const handleEmailInputChange = (value: string) => {
    setEmailAddress(value);
  };

  const handleSmsInputChange = (value: string) => {
    setPhoneNumber(value);
  };

  if (isDataLoading) {
    return <PayableCashSkeleton visible={true}></PayableCashSkeleton>;
  }

  return (
    <div className="rcp-container">
      {/* banner for generic info and error */}
      <div className="ez-pay-balance-banner" role={getBannerRole("success")}>
        {emailBannerState && (
          <Banner
            autoCloseTimer={8000}
            messageLabel={t("cash-barcode-sent", {
              phoneOrEmail: emailAddress,
            })}
            variant="success"
            data-cy="banner-success-content"
            withIcon
            withClose
          />
        )}
        {smsBannerState && (
          <Banner
            autoCloseTimer={8000}
            messageLabel={t("cash-barcode-sent", {
              phoneOrEmail: phoneNumber,
            })}
            variant="success"
            data-cy="banner-success-content"
            withIcon
            withClose
          />
        )}
      </div>
      <div className="payable-rcp-barcode-view-frame payable-cash">
        <div
          className="common-header payment-receipt-frame-header"
          style={{ width: "100%", border: "none", paddingBottom: "0px" }}
        ></div>
        <div className="ez-pay-balance-top">
          <div className="payable-card-header">
            <PayableBreadcrumb isCashView={true} />
          </div>
          {isRcpOnly && (
            <>
              <div className="ez-pay-balance-divider"></div>
              <div className="ez-pay-balance-row">
                <div className="ez-pay-balance-content-center-amount-due-row">
                  {t("amount-due")}
                  <Tooltip
                    direction={
                      isMobile ? TooltipEnums.TopLeft : TooltipEnums.Right
                    }
                    content={t("amount-due-tooltip")}
                    classNames="amount-due-row-tooltip"
                    data-cy="amount-due-tooltip"
                    variant="rich"
                  />
                </div>
                {payableSession?.payable?.payableAmount && (
                  <div className="ez-pay-balance-content-center">
                    <div
                      className="ez-pay-balance-label-large"
                      data-cy="final-amount-due"
                    >
                      {currencyFormatter.format(
                        payableSession?.payable?.payableAmount,
                      )}
                    </div>
                  </div>
                )}

                {payableSession?.payable?.category === "account" ||
                payableSession?.payable?.category === "invoice" ? (
                  <div className="ez-pay-balance-content-center-amount-due-row">
                    {payableSession?.payable?.category === "account"
                      ? getLabelWithKrn(
                          labelSourceWithKrn.labelsWithKrn,
                          payableSession?.payable?.typeKrn,
                          "accountNumber",
                        )
                      : getLabelWithKrn(
                          labelSourceWithKrn.labelsWithKrn,
                          payableSession?.payable?.typeKrn,
                          "invoiceNumber",
                        )}
                    :
                    <span
                      className="ez-pay-balance-cell-value"
                      data-cy="balance-account-number"
                    >
                      {payableSession?.payable?.category === "account"
                        ? (payableSession?.payable as AccountPayable)
                            .accountNumber
                        : (payableSession?.payable as InvoicePayable)
                            .invoiceNumber}
                    </span>
                  </div>
                ) : null}

                <div className="ez-pay-balance-content-center-small">
                  {payableSession?.payable?.category === "invoice" && (
                    <div className="ez-pay-balance-content-cell">
                      {t("invoiced-amount")}:
                      <span
                        className="ez-pay-balance-cell-value"
                        data-cy="account-number"
                      >
                        {(payableSession?.payable as InvoicePayable)
                          .billedAmount == null
                          ? "-"
                          : currencyFormatter.format(
                              (payableSession?.payable as InvoicePayable)
                                .billedAmount ?? 0,
                            )}
                      </span>
                    </div>
                  )}
                  {payableSession?.payable?.dueDate && (
                    <div className="ez-pay-balance-content-cell">
                      {t("due-date")}:
                      <span
                        className="ez-pay-balance-cell-value"
                        data-cy="final-due-date"
                      >
                        {payableSession?.payable?.dueDate
                          ? new Intl.DateTimeFormat(i18n.language, {
                              timeZone: "UTC",
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            }).format(
                              new Date(payableSession?.payable?.dueDate),
                            )
                          : ""}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="ez-pay-balance-divider"></div>
            </>
          )}
          {
            <div className={`rcp-container${isRcpOnly ? " cash-only" : ""}`}>
              <CashView
                biller={finalBiller}
                accountNumber={
                  payableSession?.payable?.category === "account"
                    ? (payableSession?.payable as AccountPayable).accountNumber
                    : (payableSession?.payable as InvoicePayable).invoiceNumber
                }
                billerLogo={imageResources?.properties?.logoURL ?? ""}
                flowCallback={setCashView}
                realm={payableSession.instanceInfo.realm}
                emailInputChange={handleEmailInputChange}
                smsInputChange={handleSmsInputChange}
                payableAmount={
                  selectedAmountOption?.optionAmount ??
                  payableSession.payable?.payableAmount ??
                  undefined
                }
                selectedAmountOptionLabel={selectedAmountOptionLabel}
                payableTypeName={payableSession.payableType.name}
                payableKrn={payableSession?.payable?.krn}
              ></CashView>
            </div>
          }
          <ContactAndTocFooter biller={finalBiller} />
        </div>
      </div>
    </div>
  );
};
export default PayableCash;
